import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { BrandedProductName } from '../components/BrandedProductName';
import { omniAgencyFeatures } from '../data/mock';
import { 
  ArrowRight, 
  ArrowLeft,
  Handshake, 
  BarChart3, 
  Link2, 
  Wallet, 
  Shield, 
  User,
  Check,
  Zap,
  Clock,
  ChevronLeft,
  ChevronRight,
  Monitor
} from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const FeatureIcon = ({ icon }) => {
  const iconProps = { size: 28, strokeWidth: 1.5 };
  const icons = {
    handshake: <Handshake {...iconProps} />,
    chart: <BarChart3 {...iconProps} />,
    link: <Link2 {...iconProps} />,
    wallet: <Wallet {...iconProps} />,
    shield: <Shield {...iconProps} />,
    user: <User {...iconProps} />
  };
  return icons[icon] || <Zap {...iconProps} />;
};

// Product Screenshots Showcase Component
const ProductShowcase = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const screenshots = omniAgencyFeatures.screenshots;

  const nextSlide = () => {
    setActiveIndex((prev) => (prev + 1) % screenshots.length);
  };

  const prevSlide = () => {
    setActiveIndex((prev) => (prev - 1 + screenshots.length) % screenshots.length);
  };

  return (
    <section className="section relative overflow-hidden bg-[#0A0A0A]">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#D3FF62]/5 rounded-full blur-[150px] pointer-events-none" />
      </div>

      <div className="container relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#D3FF62]/30 bg-[#D3FF62]/10 mb-6"
          >
            <Monitor size={16} className="text-[#D3FF62]" />
            <span className="text-[#D3FF62] text-sm font-medium">Platform Preview</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="display-large text-white mb-4"
          >
            SEE IT IN ACTION
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="body-large text-white/70"
          >
            A modern, intuitive interface designed for sports professionals
          </motion.p>
        </div>

        {/* Screenshot Carousel */}
        <div className="relative max-w-6xl mx-auto">
          {/* Main Screenshot Display */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl"
          >
            {/* Browser Chrome */}
            <div className="bg-[#1a1a1a] px-4 py-3 flex items-center gap-2 border-b border-white/10">
              <div className="flex gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500/80" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                <div className="w-3 h-3 rounded-full bg-green-500/80" />
              </div>
              <div className="flex-1 flex justify-center">
                <div className="px-4 py-1 rounded-md bg-white/5 text-white/50 text-sm">
                  app.omniagency.io
                </div>
              </div>
            </div>

            {/* Screenshot */}
            <div className="relative bg-[#0f0f0f]">
              <AnimatePresence initial={false}>
                <motion.img
                  key={activeIndex}
                  src={screenshots[activeIndex].image}
                  alt={screenshots[activeIndex].title}
                  className="w-full h-auto"
                  loading="lazy"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, position: 'absolute', top: 0, left: 0 }}
                  transition={{ duration: 0.2 }}
                />
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all z-10"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all z-10"
          >
            <ChevronRight size={24} />
          </button>
        </div>

        {/* Screenshot Info & Thumbnails */}
        <div className="mt-8 max-w-6xl mx-auto">
          {/* Current Screenshot Info */}
          <motion.div
            key={activeIndex}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h3 className="text-2xl font-bold text-white mb-2">
              {screenshots[activeIndex].title}
            </h3>
            <p className="text-white/60 max-w-xl mx-auto">
              {screenshots[activeIndex].description}
            </p>
          </motion.div>

          {/* Thumbnail Navigation */}
          <div className="flex items-center justify-start md:justify-center gap-3 overflow-x-auto pb-4 px-4 -mx-4 md:mx-0 md:px-0 scrollbar-hide">
            {screenshots.map((screenshot, index) => (
              <button
                key={screenshot.id}
                onClick={() => setActiveIndex(index)}
                className={`relative rounded-lg overflow-hidden border-2 transition-all duration-300 flex-shrink-0 ${
                  index === activeIndex 
                    ? 'border-[#D3FF62] scale-105' 
                    : 'border-white/10 opacity-50 hover:opacity-80'
                }`}
              >
                <div className="w-20 md:w-24 overflow-hidden">
                  <img
                    src={screenshot.image}
                    alt={screenshot.title}
                    className="w-full h-auto"
                    style={{ marginTop: '-5%' }}
                  />
                </div>
                {index === activeIndex && (
                  <div className="absolute inset-0 bg-[#D3FF62]/10" />
                )}
              </button>
            ))}
          </div>

          {/* Dots Indicator (Mobile) */}
          <div className="flex items-center justify-center gap-2 mt-6 md:hidden">
            {screenshots.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === activeIndex ? 'w-6 bg-[#D3FF62]' : 'bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const OmniAgencyPage = () => {
  const heroRef = useRef(null);
  const featuresRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start']
  });

  const heroY = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  useEffect(() => {
    // Animate features on scroll
    const featureCards = document.querySelectorAll('.feature-card');
    featureCards.forEach((card, index) => {
      gsap.fromTo(
        card,
        { y: 60, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          delay: index * 0.1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: card,
            start: 'top 85%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Navigation */}
      <Navbar />

      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#004534]/30 via-[#0A0A0A] to-[#0A0A0A]" />
        <div className="grid-pattern" />
        
        {/* Gradient Orbs */}
        <div className="absolute top-1/4 -left-32 w-[500px] h-[500px] bg-[#D3FF62]/15 rounded-full blur-[150px] pointer-events-none" />
        <div className="absolute bottom-1/4 -right-32 w-[400px] h-[400px] bg-[#004534]/30 rounded-full blur-[120px] pointer-events-none" />

        <motion.div 
          style={{ y: heroY, opacity: heroOpacity }}
          className="container relative z-10 text-center"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D3FF62] text-[#004534] mb-8"
          >
            <Zap size={16} />
            <span className="text-sm font-bold">AVAILABLE NOW</span>
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-[clamp(3rem,10vw,8rem)] text-white leading-none tracking-[-0.04em] mb-6"
          >
            <span className="font-black">OMNI</span><span className="font-extralight">AGENCY</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-2xl md:text-3xl font-medium text-[#D3FF62] mb-6"
          >
            {omniAgencyFeatures.hero.subtitle}
          </motion.p>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="body-large text-white/70 max-w-2xl mx-auto mb-10"
          >
            {omniAgencyFeatures.hero.description}
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex items-center justify-center"
          >
            <Link to="/demo" className="btn-primary text-lg px-8 py-4">
              Request Demo
              <ArrowRight size={20} />
            </Link>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-16 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
            className="w-6 h-10 rounded-full border-2 border-white/30 flex justify-center pt-2"
          >
            <div className="w-1.5 h-1.5 rounded-full bg-[#D3FF62]" />
          </motion.div>
        </motion.div>
      </section>

      {/* Product Showcase Section */}
      <ProductShowcase />

      {/* Features Section */}
      <section id="features" className="section" ref={featuresRef}>
        <div className="container">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-[#D3FF62] font-semibold uppercase tracking-widest mb-4">
              Powerful Features
            </p>
            <h2 className="display-large text-white mb-6">
              STREAMLINE YOUR<br />
              <span className="gradient-text">WORKFLOW</span>
            </h2>
            <p className="body-large text-white/70 mb-4">
              Built by sports professionals, for sports professionals. Improve collaboration, manage athletes efficiently, and scale your agency with tools designed for real-world workflows.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {omniAgencyFeatures.features.map((feature, index) => (
              <div 
                key={index}
                className="feature-card p-8 rounded-3xl bg-[#141414] border border-white/10 hover:border-[#D3FF62]/30 transition-all duration-300 card-hover"
              >
                {/* Icon */}
                <div className="w-14 h-14 rounded-2xl bg-[#D3FF62]/10 flex items-center justify-center mb-6 text-[#D3FF62]">
                  <FeatureIcon icon={feature.icon} />
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-white mb-3">
                  {feature.title}
                </h3>

                {/* Description */}
                <p className="text-white/70 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section bg-[#D3FF62]">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <div>
              <p className="text-[#004534]/60 font-semibold uppercase tracking-widest mb-4">
                Why <BrandedProductName product="OMNIAGENCY" />
              </p>
              <h2 className="text-[clamp(2rem,5vw,3.5rem)] font-bold text-[#004534] leading-tight mb-8">
                Built for the Modern Agent
              </h2>
              <p className="text-lg text-[#004534]/80 mb-8">
                The NIL landscape moves fast. <BrandedProductName product="OMNIAGENCY" /> keeps all your athlete data, contracts, and deals in one place—giving you instant access to the information you need to represent your clients effectively and close deals faster.
              </p>

              {/* Benefits List */}
              <div className="space-y-4">
                {omniAgencyFeatures.benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-6 h-6 rounded-full bg-[#004534] flex items-center justify-center flex-shrink-0">
                      <Check size={14} className="text-[#D3FF62]" />
                    </div>
                    <span className="text-[#004534] font-medium">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Visual */}
            <div className="relative">
              <div className="aspect-square rounded-3xl bg-[#004534] border-2 border-[#004534] flex items-center justify-center">
                <div className="text-center">
                  <img 
                    src="/logo-compact.png" 
                    alt="OSG" 
                    className="w-32 h-32 object-contain mx-auto mb-6"
                  />
                  <p className="text-white text-xl"><span className="font-black">OMNI</span><span className="font-extralight">AGENCY</span></p>
                  <p className="text-white/60 text-sm">Athlete Representation Platform</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="section">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              whileInView={{ scale: [0.8, 1] }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#D3FF62]/30 bg-[#D3FF62]/10 mb-8"
            >
              <Zap size={16} className="text-[#D3FF62]" />
              <span className="text-[#D3FF62] text-sm font-medium">Available Now</span>
            </motion.div>

            <h2 className="display-large text-white mb-6">
              READY TO TRANSFORM<br />
              <span className="gradient-text">YOUR AGENCY?</span>
            </h2>

            <p className="body-large text-white/70 mb-12 max-w-2xl mx-auto">
              Join the agents and collectives already using <BrandedProductName product="OMNIAGENCY" /> to close more deals, streamline operations, and provide better service to their athletes.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/demo"
                className="btn-primary text-lg px-10 py-5"
              >
                Request Demo
                <ArrowRight size={20} />
              </Link>
              <motion.a
                href="mailto:sales@omnisportsgroup.us"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="btn-secondary text-lg px-10 py-5"
              >
                Contact Sales
              </motion.a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-white/10">
        <div className="container">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <Link to="/" onClick={() => window.scrollTo(0, 0)}>
              <img 
                src="/logo-full.png" 
                alt="OmniSportsGroup" 
                className="h-8 w-auto"
              />
            </Link>
            <p className="text-white/50 text-sm">
              © 2026 OmniSportsGroup. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default OmniAgencyPage;
